import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminInquiryList, replyAdminInquiry } from '../../lib/api/notice-support-api';
import './CustomerServiceManagement.css';

function CustomerServiceManagement() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [answerContent, setAnswerContent] = useState('');
  const [saving, setSaving] = useState(false);

  const selected = useMemo(() => rows.find((row) => row.id === selectedId) || null, [rows, selectedId]);

  const loadRows = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getAdminInquiryList(token);
      const list = response?.data || [];
      setRows(list);
      if (list.length > 0) {
        const nextId = selectedId && list.some((row) => row.id === selectedId) ? selectedId : list[0].id;
        setSelectedId(nextId);
        const next = list.find((row) => row.id === nextId);
        setAnswerContent(next?.answerContent || '');
      } else {
        setSelectedId(null);
        setAnswerContent('');
      }
    } catch (err) {
      setError(err.message || '고객센터 관리 목록 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSelect = (row) => {
    setSelectedId(row.id);
    setAnswerContent(row.answerContent || '');
  };

  const handleReply = async () => {
    if (!selected || !answerContent.trim()) {
      return;
    }

    try {
      setSaving(true);
      const response = await replyAdminInquiry(token, selected.id, answerContent.trim());
      if (response?.success) {
        await loadRows();
      }
    } catch (err) {
      setError(err.message || '답변 등록에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="customer-service-management">
      {loading && <div className="admin-table-status">불러오는 중입니다...</div>}
      {!!error && <div className="admin-table-status error">{error}</div>}

      <table className="admin-basic-table">
        <thead>
          <tr>
            <th style={{ width: '70px' }}>번호</th>
            <th style={{ width: '120px' }}>요청자</th>
            <th>제목</th>
            <th style={{ width: '110px' }}>상태</th>
            <th style={{ width: '160px' }}>접수일시</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item, index) => (
            <tr
              key={item.id}
              className={item.id === selectedId ? 'admin-selected-row' : ''}
              onClick={() => handleSelect(item)}
            >
              <td>{rows.length - index}</td>
              <td>{item.requester}</td>
              <td>{item.title}</td>
              <td>{item.status}</td>
              <td>{item.receivedAt}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="admin-empty-row">데이터가 없습니다.</td>
            </tr>
          )}
        </tbody>
      </table>

      {selected && (
        <div className="admin-inquiry-detail">
          <div className="admin-inquiry-head">
            <h3>{selected.title}</h3>
            <span>{selected.requester} | {selected.receivedAt}</span>
          </div>

          <div className="admin-inquiry-body">
            {selected.content}
          </div>

          <div className="admin-reply-box">
            <label htmlFor="answerContent">답변</label>
            <textarea
              id="answerContent"
              value={answerContent}
              onChange={(e) => setAnswerContent(e.target.value)}
              rows={6}
              placeholder="답변 내용을 입력하세요"
            />
            <div className="admin-reply-meta">
              <span>
                {selected.answeredAt ? `최종답변: ${selected.answeredAt} (${selected.answeredBy || 'admin'})` : '아직 답변이 없습니다.'}
              </span>
              <button type="button" onClick={handleReply} disabled={saving || !answerContent.trim()}>
                {saving ? '저장 중...' : '답변 저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerServiceManagement;
